import { Fragment, useState } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { packClasses } from "Util";
import { Link } from "react-router-dom";
import { AppContext } from "context/App";
import { useAuth } from "hooks/Auth";
import {
    MenuIcon,
    XIcon,
    DocumentTextIcon,
    NewspaperIcon,
    UserIcon,
    UsersIcon,
    ArrowRightIcon,
    SpeakerphoneIcon,
} from "@heroicons/react/outline";
import flatCube from "assets/flat-cube.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular } from "@fortawesome/fontawesome-svg-core/import.macro";

export default function MainLayout(props) {
    const user = props.user ? props.user : {};
    const auth = useAuth();
    const [currentNav, setCurrentNav] = useState("home");

    const navigation = [
        {
            name: "Dashboard",
            to: "/app/dashboard",
            icon: DocumentTextIcon,
            isCurrent: (currentNav) =>
                currentNav === "dashboard" || currentNav === "home",
        },
        {
            name: "Profile",
            to: "/app/profile",
            isCurrent: (currentNav) => currentNav === "profile",
        },
        {
            name: "Users",
            to: "/app/user",
            icon: UserIcon,
            isCurrent: (currentNav) => currentNav === "user",
        },
        {
            name: "Groups",
            to: "/app/group",
            icon: UsersIcon,
            isCurrent: (currentNav) => currentNav === "group",
        },
        {
            name: "Publication",
            to: "/app/publication",
            icon: NewspaperIcon,
            isCurrent: (currentNav) => currentNav === "publication",
        },
    ];

    const userNavigation = [
        (active) => (
            <button
                onClick={props.requestPublicationChange}
                className={packClasses(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-sm text-gray-700"
                )}
            >
                Change Publication
            </button>
        ),
        (active) => (
            <Link
                to="/app/profile"
                className={packClasses(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-sm text-gray-700"
                )}
            >
                Your Profile
            </Link>
        ),
        (active) => (
            <button
                onClick={props.requestLogout}
                className={packClasses(
                    active ? "bg-gray-100" : "",
                    "block w-full py-2 px-4 text-left text-sm text-gray-700"
                )}
            >
                Sign out
            </button>
        ),
    ];

    return (
        <AppContext.Provider value={{ user, setCurrentNav }}>
            <div>
                <Disclosure as="nav" className="bg-white">
                    {({ open }) => (
                        <Fragment>
                            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                                <div className="relative flex items-center justify-between h-16">
                                    <div className="flex items-center px-2 lg:px-0">
                                        <div className="flex-shrink-0">
                                            <img
                                                className="block h-8 w-auto"
                                                src={flatCube}
                                                alt="SNworks"
                                            />
                                        </div>
                                        <div className="hidden lg:block lg:ml-6">
                                            <div className="flex space-x-4">
                                                {navigation.map((item) => (
                                                    <Link
                                                        key={item.name}
                                                        to={
                                                            item.to
                                                                ? item.to
                                                                : "/app"
                                                        }
                                                        aria-current={
                                                            item.isCurrent(
                                                                currentNav
                                                            )
                                                                ? "page"
                                                                : undefined
                                                        }
                                                        className={packClasses(
                                                            item.isCurrent(
                                                                currentNav
                                                            )
                                                                ? "bg-gray-200 text-gray-800 px-3 py-2 rounded-md text-sm font-medium"
                                                                : "text-gray-800 hover:bg-gray-200 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                                                        )}
                                                    >
                                                        {item.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex lg:hidden">
                                        {/* Mobile menu button */}
                                        <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-600">
                                            <span className="sr-only">
                                                Open main menu
                                            </span>
                                            {open ? (
                                                <XIcon
                                                    className="block h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            ) : (
                                                <MenuIcon
                                                    className="block h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </Disclosure.Button>
                                    </div>
                                    <div className="hidden lg:block lg:ml-4">
                                        <div className="flex items-center">
                                            <button
                                                type="button"
                                                className="flex-shrink-1 p-0.5 rounded-full text-green-600 focus:outline-none focus:ring-2 focus:ring-green-600"
                                                onClick={() =>
                                                    window.open(
                                                        "https://snworks.zendesk.com/hc/en-us/requests/new",
                                                        "_blank"
                                                    )
                                                }
                                            >
                                                <span className="sr-only">
                                                    Feedback
                                                </span>
                                                <SpeakerphoneIcon
                                                    className="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>

                                            {/* Profile dropdown */}
                                            <Menu
                                                as="div"
                                                className="ml-4 relative flex-shrink-0"
                                            >
                                                <div>
                                                    <Menu.Button className="bg-white rounded-full flex text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600">
                                                        <span className="sr-only">
                                                            Open user menu
                                                        </span>
                                                        <img
                                                            className="h-8 w-8 rounded-full"
                                                            src={user.image_url}
                                                            alt=""
                                                        />
                                                    </Menu.Button>
                                                </div>
                                                <Transition
                                                    as={Fragment}
                                                    enter="transition ease-out duration-100"
                                                    enterFrom="transform opacity-0 scale-95"
                                                    enterTo="transform opacity-100 scale-100"
                                                    leave="transition ease-in duration-75"
                                                    leaveFrom="transform opacity-100 scale-100"
                                                    leaveTo="transform opacity-0 scale-95"
                                                >
                                                    <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                                                        {userNavigation.map(
                                                            (item, index) => (
                                                                <Menu.Item
                                                                    key={index}
                                                                >
                                                                    {({
                                                                        active,
                                                                    }) =>
                                                                        item(
                                                                            active
                                                                        )
                                                                    }
                                                                </Menu.Item>
                                                            )
                                                        )}
                                                    </Menu.Items>
                                                </Transition>
                                            </Menu>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Disclosure.Panel className="lg:hidden">
                                <div className="px-2 pt-2 pb-3 space-y-1">
                                    {navigation.map((item) => (
                                        <Disclosure.Button
                                            as={Link}
                                            key={item.name}
                                            to={item.to ? item.to : "/app"}
                                            aria-current={
                                                item.isCurrent(currentNav)
                                                    ? "page"
                                                    : undefined
                                            }
                                            className={packClasses(
                                                item.isCurrent(currentNav)
                                                    ? "bg-gray-200 text-gray-800 block px-3 py-2 rounded-md text-base font-medium"
                                                    : "text-gray-800 hover:bg-gray-200 block px-3 py-2 rounded-md text-base font-medium"
                                            )}
                                        >
                                            {item.name}
                                        </Disclosure.Button>
                                    ))}
                                </div>
                                <div className="pt-4 pb-3 border-t border-gray-700">
                                    <div className="flex items-center px-5">
                                        <div className="flex-shrink-0">
                                            <img
                                                className="h-10 w-10 rounded-full"
                                                src={user.image_url}
                                                alt=""
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <div className="text-base font-medium text-gray-800">
                                                {user.name}
                                            </div>
                                            <div className="text-sm font-medium text-gray-800">
                                                {user.email}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="ml-auto flex-shrink-0 bg-green-600 p-1 rounded-full text-gray-100 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-white"
                                            onClick={() =>
                                                window.open(
                                                    "https://snworks.zendesk.com/hc/en-us/requests/new",
                                                    "_blank"
                                                )
                                            }
                                        >
                                            <span className="sr-only">
                                                Help
                                            </span>
                                            <SpeakerphoneIcon
                                                className="h-6 w-6"
                                                aria-hidden="true"
                                            />
                                        </button>
                                    </div>
                                    <div className="mt-3 px-2 space-y-1">
                                        {userNavigation.map((item, index) => (
                                            <Disclosure.Button key={index}>
                                                {({ active }) => item(active)}
                                            </Disclosure.Button>
                                        ))}
                                    </div>
                                </div>
                            </Disclosure.Panel>
                        </Fragment>
                    )}
                </Disclosure>
                <div className="text-center py-2 bg-gray-700 text-white text-sm sticky top-0 z-40">
                    Viewing <strong>{auth.publication.name}</strong> -{" "}
                    <button
                        onClick={props.requestPublicationChange}
                        className="underline inline-flex items-center"
                    >
                        Change publication{" "}
                        <ArrowRightIcon className="ml-1 h-4 w-4" />
                    </button>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
                    {props.children}
                </div>

                <div className="border-warm-gray-700 mt-4 border-t pt-8 text-center">
                    <p className="font-mono text-xs text-gray-500">
                        ID: {process.env.REACT_APP_APP_ID}
                        &nbsp;&nbsp; VER: {process.env.REACT_APP_APP_VER}
                    </p>
                    <p className="mt-5 text-xs text-gray-500">
                        Made with <FontAwesomeIcon icon={regular("heart")} /> in{" "}
                        <FontAwesomeIcon icon={regular("mitten")} />
                        <span className="px-2">&middot;</span>
                        Copyright {new Date().getFullYear()} SNworks/The State
                        News
                    </p>
                </div>
            </div>
        </AppContext.Provider>
    );
}
